/**
 * Jobs API Module
 * All job-related API endpoints
 */

import { api } from './client.js';

/**
 * Jobs API endpoints
 */
export const jobsApi = {
  /**
   * Get all jobs with optional filters and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 10, max: 500)
   * @param {string} params.status - Filter by status (pending, imported, rejected)
   * @param {string} params.source - Filter by source
   * @param {string} params.jobType - Filter by job type
   * @param {string} params.search - Search term for title, company, description, or jobscribe_id
   * @returns {Promise<Object>} Jobs data with pagination
   */
  getAll: (params = {}) => {
    // Filter out undefined/null values
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    const queryParams = new URLSearchParams(cleanParams).toString();
    const endpoint = `/admin/jobs${queryParams ? `?${queryParams}` : ''}`;
    return api.get(endpoint);
  },

  /**
   * Get job by ID
   * @param {string|number} id - Job ID
   * @returns {Promise<Object>} Job data
   */
  getById: (id) => {
    if (!id) {
      return Promise.reject(new Error('Job ID is required'));
    }
    return api.get(`/admin/jobs/${id}`);
  },

  /**
   * Get job by JobScribe ID
   * @param {string} jobscribeId - JobScribe ID (e.g., JS-JOB-0001)
   * @returns {Promise<Object>} Job data
   */
  getByJobscribeId: (jobscribeId) => {
    if (!jobscribeId) {
      return Promise.reject(new Error('JobScribe ID is required'));
    }
    return api.get(`/admin/jobs/jobscribe/${jobscribeId}`);
  },

  /**
   * Create new job
   * @param {Object} jobData - Job data
   * @param {string} jobData.title - Job title (required)
   * @param {string} jobData.company - Company name (required)
   * @param {string} [jobData.location] - Location
   * @param {string} [jobData.jobType] - Job type (Full-time, Part-time, Contract, Internship, Temporary)
   * @param {string} [jobData.jobMode] - Job mode (Remote, On-site, Hybrid)
   * @param {string} [jobData.salaryRange] - Salary range
   * @param {string} [jobData.experienceRequired] - Experience required
   * @param {string} [jobData.description] - Job description
   * @param {Array<string>} [jobData.skills] - Skills array
   * @param {string} [jobData.source] - Source (default: Manual Entry)
   * @param {string} [jobData.jobUrl] - Job URL
   * @param {string} [jobData.externalJobId] - External job ID
   * @param {string} [jobData.postedDate] - Posted date (ISO format)
   * @param {string} [jobData.lastApplicationDate] - Last application date (ISO format)
   * @param {string} [jobData.status] - Status (pending, imported, rejected)
   * @returns {Promise<Object>} Created job data
   */
  create: (jobData) => {
    if (!jobData || !jobData.title || !jobData.company) {
      return Promise.reject(new Error('Job title and company are required'));
    }
    return api.post('/admin/jobs', jobData);
  },

  /**
   * Check for an existing live catalog job with the same content fingerprint.
   * @param {Object} params
   * @returns {Promise<{ matches: Array, fingerprint: string|null }>}
   */
  checkContentDuplicate: (params = {}) => {
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value === undefined || value === null || value === '') return acc;
      if (Array.isArray(value)) {
        acc[key] = JSON.stringify(value);
      } else {
        acc[key] = value;
      }
      return acc;
    }, {});
    const queryParams = new URLSearchParams(cleanParams).toString();
    return api.get(`/admin/jobs/duplicate-check${queryParams ? `?${queryParams}` : ''}`);
  },

  /**
   * Update job
   * @param {string|number} id - Job ID
   * @param {Object} jobData - Updated job data (all fields optional)
   * @returns {Promise<Object>} Updated job data
   */
  update: (id, jobData) => {
    if (!id) {
      return Promise.reject(new Error('Job ID is required'));
    }
    if (!jobData || Object.keys(jobData).length === 0) {
      return Promise.reject(new Error('Job data is required'));
    }
    return api.put(`/admin/jobs/${id}`, jobData);
  },

  /**
   * Delete job (soft delete)
   * @param {string|number} id - Job ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  delete: (id) => {
    if (!id) {
      return Promise.reject(new Error('Job ID is required'));
    }
    return api.delete(`/admin/jobs/${id}`);
  },

  /**
   * Update job status
   * @param {string|number} id - Job ID
   * @param {string} status - New status (pending, imported, rejected)
   * @returns {Promise<Object>} Updated job data
   */
  updateStatus: (id, status) => {
    if (!id) {
      return Promise.reject(new Error('Job ID is required'));
    }
    if (!status) {
      return Promise.reject(new Error('Status is required'));
    }
    return api.patch(`/admin/jobs/${id}/status`, { status });
  },

  /**
   * Bulk update job statuses
   * @param {number[]} ids - Job IDs to update
   * @param {string} status - New status (pending, imported, rejected)
   * @returns {Promise<Object>} Summary with updated, skipped, notFound counts
   */
  bulkUpdateStatus: (ids, status) => {
    if (!Array.isArray(ids) || ids.length === 0) {
      return Promise.reject(new Error('At least one job id is required'));
    }
    if (!status) {
      return Promise.reject(new Error('Status is required'));
    }
    return api.patch('/admin/jobs/bulk/status', { ids, status });
  },

  /**
   * Get job statistics overview
   * @returns {Promise<Object>} Job statistics
   */
  getStats: () => {
    return api.get('/admin/jobs/stats/overview');
  },

  /**
   * Get all jobs for export (no pagination, respects filters)
   * @param {Object} params - Filter parameters (status, source, jobType, search)
   * @returns {Promise<Array>} All matching jobs
   */
  exportAll: (params = {}) => {
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    const queryParams = new URLSearchParams(cleanParams).toString();
    const endpoint = `/admin/jobs/export${queryParams ? `?${queryParams}` : ''}`;
    return api.get(endpoint);
  },

  /**
   * Get expired jobs with filters
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Expired jobs with pagination
   */
  getExpired: (params = {}) => {
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    const queryParams = new URLSearchParams(cleanParams).toString();
    const endpoint = `/admin/jobs/expired${queryParams ? `?${queryParams}` : ''}`;
    return api.get(endpoint);
  },

  /**
   * Restore/repost an expired job
   * @param {string|number} id - Job ID
   * @param {Object} data - Restore data
   * @param {string} data.lastApplicationDate - New application deadline (YYYY-MM-DD)
   * @returns {Promise<Object>} Restored job data
   */
  restore: (id, { lastApplicationDate }) => {
    if (!id) {
      return Promise.reject(new Error('Job ID is required'));
    }
    if (!lastApplicationDate) {
      return Promise.reject(new Error('Last application date is required'));
    }
    return api.post(`/admin/jobs/${id}/restore`, { lastApplicationDate });
  },

  /**
   * Bulk restore/repost expired jobs
   * @param {number[]} ids - Job IDs to restore
   * @param {string} lastApplicationDate - New application deadline (YYYY-MM-DD)
   * @returns {Promise<Object>} Summary with restored, skipped, notFound counts
   */
  bulkRestore: (ids, lastApplicationDate) => {
    if (!Array.isArray(ids) || ids.length === 0) {
      return Promise.reject(new Error('At least one job id is required'));
    }
    if (!lastApplicationDate) {
      return Promise.reject(new Error('Last application date is required'));
    }
    return api.post('/admin/jobs/bulk/restore', { ids, lastApplicationDate });
  },
};

export default jobsApi;
